import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Smartphone, Landmark, Upload, CheckCircle2, Loader2, CreditCard, Clock4 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { fmtDate } from "../lib/options";
import { ChoiceCard, Spinner, Empty, PageTitle, Chip } from "../components/Bits";

function PayForm({ plan, payment, onDone }) {
  const { t, lang } = useT();
  const ref = useRef();
  const [method, setMethod] = useState("");
  const [refNo, setRefNo] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const details = method === "juice" ? payment.juice_number && `Juice : ${payment.juice_number}${payment.account_name ? ` (${payment.account_name})` : ""}`
    : method === "bank" ? payment.bank_account && `${payment.bank_name || ""} · ${payment.account_name || ""} · ${payment.bank_account}` : null;
  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("plan_id", plan.id); fd.append("method", method); fd.append("reference", refNo); fd.append("file", file);
    try { await api.post("/employer/subscription", fd); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 space-y-3 fade-up" data-testid="pay-form">
      <h2 className="text-xl font-bold text-[color:var(--navy)]">{t("pay_how")}</h2>
      <ChoiceCard icon={Smartphone} label={t("pay_juice")} selected={method === "juice"} onClick={() => setMethod("juice")} testid="pay-method-juice" />
      <ChoiceCard icon={Landmark} label={t("pay_bank")} selected={method === "bank"} onClick={() => setMethod("bank")} testid="pay-method-bank" />
      {method && (
        <>
          <div className="rounded-2xl bg-slate-50 p-4" data-testid="pay-details">
            <p className="font-semibold text-slate-600">{t("pay_details")}</p>
            <p className="text-2xl font-extrabold text-[color:var(--navy)]">Rs {plan.price_mur.toLocaleString()}</p>
            <p className="font-semibold">{details || t("pay_not_set")}</p>
            {payment[`instructions_${lang}`] && <p className="mt-2 text-sm text-slate-600">{payment[`instructions_${lang}`]}</p>}
          </div>
          <label className="label pt-2" htmlFor="pay-ref">{t("pay_ref")}</label>
          <input id="pay-ref" className="field" value={refNo} onChange={(e) => setRefNo(e.target.value)} data-testid="pay-reference-input" />
          <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="payment-upload-input" />
          <ChoiceCard icon={file ? CheckCircle2 : Upload} label={file ? file.name : t("pay_proof")} selected={!!file} onClick={() => ref.current.click()} testid="pay-proof-card" />
          <button className="btn-orange !w-full" disabled={!file || busy} onClick={submit} data-testid="payment-submit-btn">{busy && <Loader2 className="animate-spin" />}{t("pay_send")}</button>
        </>
      )}
    </div>
  );
}

export default function Subscription() {
  const { t, lang } = useT();
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [plan, setPlan] = useState(null);
  const [sent, setSent] = useState(false);
  const load = () => {
    api.get("/employer/subscription").then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setD({ history: [] }); });
    api.get("/public/config").then((r) => setCfg(r.data)).catch(() => setCfg({ plans: [], payment: {} }));
  };
  useEffect(() => { load(); }, []);
  if (!d || !cfg) return <Spinner />;
  const pending = d.history.some((h) => h.status === "pending");
  return (
    <div className="narrow pb-12">
      <Link to="/employer" className="mt-4 inline-flex min-h-[44px] items-center gap-2 font-semibold text-slate-600" data-testid="sub-back-btn"><ArrowLeft size={20} />{t("back")}</Link>
      <PageTitle>{t("sub_t")}</PageTitle>
      {d.active && <div className="mb-4 rounded-2xl bg-emerald-50 p-4 font-semibold text-emerald-800" data-testid="sub-active">{d.active.plans?.[`name_${lang}`]} · {t("plan_active")} {fmtDate(d.active.ends_at, lang)}</div>}
      {sent || pending ? (
        <div className="card-soft flex flex-col items-center p-8 text-center" data-testid="sub-pending">
          {sent ? <CheckCircle2 size={56} className="text-emerald-500" /> : <Clock4 size={56} className="text-amber-500" />}
          <p className="mt-3 text-xl font-bold text-[color:var(--navy)]">{t("pay_sent_t")}</p>
          <p className="text-slate-600">{t("pay_sent_sub")}</p>
        </div>
      ) : cfg.plans.length === 0 ? <Empty icon={CreditCard} title={t("no_plans_t")} sub={t("no_plans_sub")} testid="sub-no-plans" /> : (
        <>
          <div className="space-y-3">
            {cfg.plans.map((p) => (
              <ChoiceCard key={p.id} icon={CreditCard} label={`${p[`name_${lang}`]} — Rs ${p.price_mur.toLocaleString()}`} sub={`${p.duration_days} ${t("per")}${p.candidate_search ? ` · ${t("search_included")}` : ""}`}
                selected={plan?.id === p.id} onClick={() => setPlan(p)} testid={`plan-card-${p.id}`} />
            ))}
          </div>
          {plan && <PayForm plan={plan} payment={cfg.payment || {}} onDone={() => { setSent(true); load(); }} />}
        </>
      )}
      {d.history.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-lg font-bold text-[color:var(--navy)]">{t("history")}</h2>
          <div className="space-y-2">
            {d.history.map((h) => (
              <div key={h.id} className="card-soft flex items-center justify-between p-4" data-testid={`sub-history-${h.id}`}>
                <div><p className="font-bold">{h.plans?.[`name_${lang}`]} · Rs {h.amount_mur?.toLocaleString()}</p><p className="text-sm text-slate-500">{fmtDate(h.created_at, lang)} · {h.method === "juice" ? "Juice" : t("pay_bank")}</p>{h.admin_note && <p className="text-sm text-slate-600">{h.admin_note}</p>}</div>
                <Chip status={h.status} label={t(`s_${h.status}`)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
